import { BannerStack, type BannerStackHandle } from "$root/components/banner-stack/BannerStack";
import { sortBanners } from "$root/components/banner-stack/sortBanners";
import type { BannerItem } from "$root/components/banner-stack/types";
import { type IUser, useUserValue } from "$root/functional-areas/user";
import type { FormatDate } from "$root/localization/formatters";
import { useLocaleFormatters } from "$root/localization/hooks";
import type { AgGridReact } from "ag-grid-react";
import type { TFunction } from "i18next";
import { type Ref, useImperativeHandle, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { InstrumentEditorBuilderProps } from "../builder";
import type { Instrument } from "../const";
import type { InstrumentCompositionEditorProps } from "../instrumentEditor.types";
import type { QuantityEditorBuilderProps } from "../quantity/builder";
import { heldBlockedBanner } from "../quantity/held-issues/heldBlockedBanner";
import { unpricedRowsBanner } from "../quantity/market-date/unpricedRowsBanner";
import { negativeCashBanner } from "../quantity/negativeCashBanner";
import { checkCounts } from "../quantity/new-instruments/checks";
import { newInstrumentIssuesBanner } from "../quantity/new-instruments/newInstrumentIssuesBanner";
import { unvaluedRowsBanner } from "../quantity/position-sizing/unvaluedRowsBanner";
import { resizedRowsBanner } from "../quantity/sizing-message/resizedRowsBanner";
import { instrumentLimitReachedBanner } from "./instrumentLimitReachedBanner";
import { minNumberOfInstrumentBanner } from "./minNumberOfInstrumentBanner";
import { normalisedInvestmentInstrumentBanner } from "./normalisedInvestmentInstrumentBanner";
import { normaliseAndExcessToCashBanner } from "./normaliseAndExcessToCashBanner";

export type EditorBannersProps = InstrumentCompositionEditorProps & {
	hasRows: boolean;
	/** The rows the grid shows, search filter applied. */
	rows: Instrument[];
	selection: Instrument[];
	agGrid: AgGridReact<Instrument> | null;
	/** Lets the page's save buttons show the banner that blocks the save. */
	handleRef?: Ref<EditorBannersHandle>;
};

export type EditorBannersHandle = {
	/**
	 * Pulses the topmost banner that blocks a save and answers it, so the caller can say why the save
	 * did not go through. Without one it changes nothing and answers `null`.
	 */
	revealBlocking(): BannerItem | null;
};

/** Everything a banner may read: the editor's state, and what the hooks below supply. */
type EditorBannerContext = EditorBannersProps & {
	stopEditing(): void;
	t: TFunction;
	formatDate: FormatDate;
	user: IUser;
};

type QuantityContext = EditorBannerContext & { instrumentBuilder: QuantityEditorBuilderProps };
type WeightContext = EditorBannerContext & { instrumentBuilder: InstrumentEditorBuilderProps };

function always(_ctx: EditorBannerContext): _ctx is EditorBannerContext {
	return true;
}

/** A quantity composition with rows, being edited or created. */
function isQuantityComposition(ctx: EditorBannerContext): ctx is QuantityContext {
	return ctx.compositionMode === "quantity" && ctx.entity !== "UNIVERSE" && ctx.hasRows;
}

/** A weight composition with rows. A universe's instruments carry no weight to normalise. */
function canNormalise(ctx: EditorBannerContext): ctx is WeightContext {
	return ctx.compositionMode !== "quantity" && ctx.entity !== "UNIVERSE" && ctx.hasRows;
}

type EditorBannerRule = (ctx: EditorBannerContext) => { banner: BannerItem; blocksSave: boolean } | null;

/**
 * `banner` runs only in the editors `when` admits, and decides from their state whether to show.
 * `blocksSave` says whether the banner it shows keeps the composition from being saved.
 */
function rule<C extends EditorBannerContext>(
	when: (ctx: EditorBannerContext) => ctx is C,
	banner: (ctx: C) => BannerItem | null,
	blocksSave: (ctx: C) => boolean = () => false,
): EditorBannerRule {
	return (ctx) => {
		if (!when(ctx)) {
			return null;
		}
		const shown = banner(ctx);
		return shown && { banner: shown, blocksSave: blocksSave(ctx) };
	};
}

/** Added instruments the save waits on: those that could not be checked, and those that need a fix. */
function hasUnresolvedNewInstruments(ctx: QuantityContext): boolean {
	const { failed, needFix } = checkCounts(ctx.instrumentBuilder.getComposition().values());
	return failed + needFix > 0;
}

/**
 * Every banner of the composition editor. The stack orders them by severity, then by their order
 * here, so this list is the order they appear in.
 */
const editorBannerRules: EditorBannerRule[] = [
	// warning
	rule(isQuantityComposition, negativeCashBanner, () => true),
	rule(isQuantityComposition, newInstrumentIssuesBanner, hasUnresolvedNewInstruments),
	rule(isQuantityComposition, unpricedRowsBanner),
	rule(isQuantityComposition, unvaluedRowsBanner, () => true),
	rule(isQuantityComposition, heldBlockedBanner),
	rule(always, instrumentLimitReachedBanner),
	rule(canNormalise, normaliseAndExcessToCashBanner),
	// info
	rule(isQuantityComposition, resizedRowsBanner),
	rule(always, minNumberOfInstrumentBanner),
	rule(always, normalisedInvestmentInstrumentBanner),
];

/** The editor's banners, in the one stack between its header row and its grid. */
export function EditorBanners({ handleRef, ...props }: EditorBannersProps): React.JSX.Element | null {
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();
	const stackRef = useRef<BannerStackHandle>(null);
	const user = useUserValue();
	const ctx: EditorBannerContext = {
		...props,
		stopEditing: () => props.agGrid?.api.stopEditing(true),
		t,
		formatDate,
		user,
	};

	// Dismissal lasts as long as the editor: the banner stays away even if its condition comes back.
	const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());

	const shown = editorBannerRules
		.map((bannerFor) => bannerFor(ctx))
		.filter((entry) => entry != null)
		.filter((entry) => !dismissed.has(entry.banner.id));
	const banners = shown.map((entry) => entry.banner);
	const blocking = new Set(shown.filter((entry) => entry.blocksSave).map((entry) => entry.banner.id));

	useImperativeHandle(handleRef, () => ({
		revealBlocking() {
			const top = sortBanners(banners).find((banner) => blocking.has(banner.id));
			return top != null && stackRef.current?.pulse(top.id) ? top : null;
		},
	}));

	// The margin belongs to the stack, so no banners means no gap either.
	if (banners.length === 0) {
		return null;
	}

	return (
		<div className="my-4">
			<BannerStack
				banners={banners}
				onDismiss={(id) => setDismissed((ids) => new Set(ids).add(id))}
				handleRef={stackRef}
			/>
		</div>
	);
}
